<template>
  <div class="instructor-details-view">
    <Sidebar />
    <div class="main-content">
      <div v-if="loading" class="loading">
        <div class="spinner"></div>
        <span>Loading instructor details...</span>
      </div>
      <div v-else-if="error" class="error">
        <i class="fas fa-exclamation-circle"></i>
        <span>{{ error }}</span>
      </div>
      <div v-else-if="instructor" class="instructor-details-container">
        <div class="header-section">
          <div class="navigation-buttons">
            <button @click="goBackToInstructors" class="back-button">
              <i class="fas fa-arrow-left"></i> Back to Instructors
            </button>
          </div>
          <div class="header-content">
            <InstructorProfile 
              :instructor="instructor"
              @edit-profile="navigateToEditPage"
            />
          </div>
        </div>

        <div class="content-grid">
          <div class="main-info">
            <InstructorBio 
              :instructor="instructor"
              :active-bio-tab="activeBioTab"
              @update-bio-tab="activeBioTab = $event"
              @update-bio="updateBio"
            />

            <InstructorExpertise :expertise="instructor.expertise" />

            <InstructorCourses :courses="instructor.courses1" />

            <InstructorSessions :sessions="instructor.sessions" />
          </div>

          <div class="side-info">
            <InstructorSessionInfo :instructor="instructor" />
            <InstructorContactInfo :instructor="instructor" />
            <InstructorStats :instructor="instructor" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Sidebar from '@/components/Sidebar.vue';
import InstructorProfile from '@/components/instructor-details/InstructorProfile.vue';
import InstructorBio from '@/components/instructor-details/InstructorBio.vue';
import InstructorExpertise from '@/components/instructor-details/InstructorExpertise.vue';
import InstructorCourses from '@/components/instructor-details/InstructorCourses.vue';
import InstructorSessions from '@/components/instructor-details/InstructorSessions.vue';
import InstructorSessionInfo from '@/components/instructor-details/InstructorSessionInfo.vue';
import InstructorContactInfo from '@/components/instructor-details/InstructorContactInfo.vue';
import InstructorStats from '@/components/instructor-details/InstructorStats.vue';

export default {
  name: 'InstructorDetailsView',
  components: {
    Sidebar,
    InstructorProfile,
    InstructorBio,
    InstructorExpertise,
    InstructorCourses,
    InstructorSessions,
    InstructorSessionInfo,
    InstructorContactInfo,
    InstructorStats
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const instructor = ref(null);
    const loading = ref(true);
    const error = ref(null);
    const activeBioTab = ref('en');

    const navigateToEditPage = () => {
      router.push(`/instructor-details/${route.params.id}/edit`);
    };

    const updateBio = async (bioData) => {
      const token = localStorage.getItem('token');
      try {
        const response = await fetch(`https://api.kewaar.com/api/admin/instructors1/${route.params.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(bioData)
        });
        if (!response.ok) throw new Error('Failed to update bio');
        await fetchInstructorDetails();
      } catch (err) {
        console.error('Error updating bio:', err);
      }
    };

    const fetchInstructorDetails = async () => {
      const token = localStorage.getItem('token');
      try {
        const response = await fetch(`https://api.kewaar.com/api/admin/instructors1/${route.params.id}`, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch instructor details');
        }

        const result = await response.json();
        instructor.value = result.data;
      } catch (err) {
        error.value = err.message;
        console.error('Error fetching instructor details:', err);
      } finally {
        loading.value = false;
      }
    };

    const goBackToInstructors = () => {
      router.push('/instructor-list');
    };

    onMounted(fetchInstructorDetails);

    return {
      instructor,
      loading,
      error,
      activeBioTab,
      navigateToEditPage,
      fetchInstructorDetails,
      updateBio,
      goBackToInstructors
    };
  }
};
</script>

<style scoped>
.instructor-details-view {
  min-height: 100vh;
  background-color: #f8fafc;
  display: flex;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  padding: 2rem;
  min-height: 100vh;
  background-color: #f8fafc;
}

.instructor-details-container {
  max-width: 1200px;
  margin: 0 auto;
}

/* Loading and Error States */
.loading, .error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #64748b;
  font-size: 1.125rem;
  gap: 1rem;
}

.error {
  color: #ef4444;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(59, 130, 246, 0.1);
  border-radius: 50%;
  border-top-color: #3b82f6;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Header Section */
.header-section {
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Content Grid Layout */
.content-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
}

.navigation-buttons {
  margin-bottom: 1.5rem;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #475569;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-button:hover {
  background-color: #e2e8f0;
  color: #334155;
}

.back-button i {
  font-size: 0.9rem;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .content-grid {
    grid-template-columns: 1.5fr 1fr;
  }
}

@media (max-width: 992px) {
  .main-content {
    margin-left: 0;
    padding: 1.5rem;
  }
  
  .content-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
}

@media (max-width: 576px) {
  .main-content {
    padding: 1rem;
  }
}
</style> 